import type { AccessGrant } from '../types';

// Derive an asset-inventory PRN for the principal of a grant, so we can
// render it as a click-through AssetName chip. Only "internal, same-account"
// IAM roles and users qualify — every other case (external account, AWS
// service, public, federated) is not in our asset inventory and would 404.
//
// PRN format mirrors what every other asset emitter in the repo uses for
// IAM principals (see assets/.../overviewConfig.tsx):
//   `prn:assets:${integrationId}:aws:iam:role:us-east-1:${arn}`.toLowerCase()

const ARN_IAM_ROLE = /^arn:aws[a-z-]*:iam::\d+:role\/.+$/;
const ARN_IAM_USER = /^arn:aws[a-z-]*:iam::\d+:user\/.+$/;

export function principalToAssetPrn(grant: AccessGrant): string | null {
  // External principals never resolve to a local asset.
  if (grant.grantOrigin === 'ext') {
    return null;
  }
  // Different AWS account = different integration, so even if it is
  // technically "internal" elsewhere, we don't have the asset here.
  if (
    grant.principalAccountId === null ||
    grant.principalAccountId !== grant.resourceAccountId
  ) {
    return null;
  }

  if (
    grant.principalType === 'aws_role' &&
    ARN_IAM_ROLE.test(grant.principal)
  ) {
    return `prn:assets:${grant.integrationId}:aws:iam:role:us-east-1:${grant.principal}`.toLowerCase();
  }

  if (
    grant.principalType === 'aws_user' &&
    ARN_IAM_USER.test(grant.principal)
  ) {
    return `prn:assets:${grant.integrationId}:aws:iam:user:us-east-1:${grant.principal}`.toLowerCase();
  }

  return null;
}
